// src/pages/competition/CompetitionCreation.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCompetition } from './CompetitionContext';
import { usePolling } from '../../context/PollingContext';
import { Loader2, Calendar, Monitor, CalendarDays, ArrowLeft, CheckCircle, AlertCircle, ChevronDown, Activity, Zap, Users, Search, User, Eye, MapPin, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import ddLogo from '../../components/ui/dd.png';
import { fetchWithAuth, JUDGES_API, AWARDS_API, API_BASE } from '../../utils/api';
import UnassignJudgeModal from '../../components/ui/UnassignJudgeModal';
import { useToast, ToastContainer } from '../../components/ui/Toast';
import { filterValidRoutines, getRoutineTypeSummary } from '../../utils/hitchkickValidation';

// HitchKick API Configuration
const HITCHKICK_CONFIG = {
  // Secure Lambda proxy endpoint (API key stored server-side)
  baseUrl: 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/hitchkick-proxy',
  environment: 'production'
};

// HitchKick data loads automatically on page load

const CompetitionCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { saveCompetitionData } = useCompetition();
  const { startSession } = usePolling();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Get role from navigation state (selected in CompetitionPortalHome)
  const selectedRole = location.state?.selectedRole || '';
  
  // Role display info
  const roleInfo = {
    'tabulator': { label: 'Tabulator (Control Competition)', icon: '🎯', color: 'blue' },
    'judge': { label: 'Judge (Score Routines)', icon: '⚖️', color: 'green' }
  };

  // Real data state
  const [events, setEvents] = useState([]);
  const [eventStages, setEventStages] = useState({});
  
  // HitchKick API state
  const [hitchkickData, setHitchkickData] = useState(null);
  const [hitchkickStages, setHitchkickStages] = useState([]);
  const [hitchkickSections, setHitchkickSections] = useState({}); // sections by stage
  const [hitchkickError, setHitchkickError] = useState(null);
  const [eventTimezone, setEventTimezone] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [loadingPercentage, setLoadingPercentage] = useState(0);
  
  // Fun loading messages that cycle through (for session start) - not used with milestone approach
  const loadingMessages = [
    { text: '🎭 WARMING UP BACKSTAGE...', percent: 15 },
    { text: '💃 STRETCHING AND PREPPING...', percent: 30 },
    { text: '🎵 CUEING THE MUSIC...', percent: 45 },
    { text: '✨ SETTING THE SPOTLIGHT...', percent: 60 },
    { text: '🌟 FINAL TOUCHES ON COSTUMES...', percent: 75 },
    { text: '🎪 OPENING THE CURTAINS...', percent: 90 },
    { text: '🎉 READY TO PERFORM!', percent: 100 }
  ];
  
  // Fun loading messages for initial HitchKick data load - not used with milestone approach
  const hitchkickLoadingMessages = [
    { text: '🎪 REGISTRATION OPENING SOON...', percent: 12 },
    { text: '🎪 CALLING ALL PERFORMERS...', percent: 25 },
    { text: '💫 GATHERING THE LINEUP...', percent: 38 },
    { text: '🎭 REVIEWING THE PROGRAM...', percent: 50 },
    { text: '✨ ORGANIZING THE SCHEDULE...', percent: 62 },
    { text: '🌟 FINAL STAGE PREPARATIONS...', percent: 75 },
    { text: '🎵 SOUND CHECK COMPLETE...', percent: 87 },
    { text: '🎉 SHOW TIME!', percent: 100 }
  ];
  
  // HitchKick Competition ID state
  const [hitchkickCompetitionId, setHitchkickCompetitionId] = useState('');
  const [competitionIdError, setCompetitionIdError] = useState('');
  
  // Selection state
  const [eventId, setEventId] = useState('');
  const [stageId, setStageId] = useState('');
  
  // Section state
  const [sectionId, setSectionId] = useState('');
  const [availableSections, setAvailableSections] = useState([]);
  const [selectedSectionData, setSelectedSectionData] = useState(null);
  
  // Judge selection state (only for judges)
  const [availableJudges, setAvailableJudges] = useState([]);
  const [selectedJudgeId, setSelectedJudgeId] = useState('');
  const [selectedJudgeProfile, setSelectedJudgeProfile] = useState(null);
  const [selectedJudgePosition, setSelectedJudgePosition] = useState('');
  const [judgesLoading, setJudgesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [failedImages, setFailedImages] = useState({});
  
  // Position conflict tracking
  const [occupiedPositions, setOccupiedPositions] = useState(new Set());
  const [assignedJudges, setAssignedJudges] = useState(new Set());
  const [positionCheckLoading, setPositionCheckLoading] = useState(false);

  // Unassign judge modal state
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [judgeToUnassign, setJudgeToUnassign] = useState(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  // Toast notifications
  const { toasts, removeToast, success, error: showError } = useToast();

  // Redirect if no role selected
  useEffect(() => {
    if (!selectedRole) {
      navigate('/competition/portal');
    }
  }, [selectedRole, navigate]);

  // Fetch available judges if role is judge
  useEffect(() => {
    const fetchJudges = async () => {
      if (selectedRole !== 'judge') return;
      
      try {
        setJudgesLoading(true);
        setError('');
        const data = await fetchWithAuth(JUDGES_API);
        
        // Format judge data similar to the old implementation
        const processedJudges = data.map(judge => {
          const judgeId = judge.SK ? judge.SK.replace("JUDGE#", "") : judge.id;
          return {
            id: judgeId,
            name: judge.name || "Unnamed Judge",
            title: judge.title || "",
            defaultNote: judge.defaultNote || "",
            photoUrl: judge.photoUrl || "",
            organization: judge.organization || "",
            experience: judge.experience || ""
          };
        });
        
        setAvailableJudges(processedJudges);
      } catch (error) {
        console.error('Error fetching judges:', error);
        setError('Failed to load judge profiles. Please check your admin settings.');
      } finally {
        setJudgesLoading(false);
      }
    };

    fetchJudges();
    
    // Also load judge settings when role changes to judge
    if (selectedRole === 'judge') {
      loadJudgeSettings();
    }
  }, [selectedRole]);
  
  // Load judge settings from admin configuration
  const loadJudgeSettings = useCallback(async () => {
    try {
      const data = await fetchWithAuth(AWARDS_API);
      const numberOfJudges = data.numberOfJudges || 3;
      // If scoringRules.maxJudges is not set, use numberOfJudges as the maximum
      // This ensures the judge position dropdown matches the configured judge count
      const maxJudges = data.scoringRules?.maxJudges || numberOfJudges || 3;
      
      setJudgeSettings({
        numberOfJudges,
        maxJudges,
        loading: false
      });
      
    } catch (error) {
      console.error('Error loading judge settings:', error);
      // Use defaults on error
      setJudgeSettings({
        numberOfJudges: 3,
        maxJudges: 3,
        loading: false
      });
    }
  }, []);

  // Update selected judge profile when judge selection changes
  useEffect(() => {
    if (selectedJudgeId && availableJudges.length > 0) {
      const judge = availableJudges.find(j => j.id === selectedJudgeId);
      setSelectedJudgeProfile(judge || null);
    } else {
      setSelectedJudgeProfile(null);
    }
  }, [selectedJudgeId, availableJudges]);

  // Handle image loading errors
  const handleImageError = (judgeId) => {
    setFailedImages(prev => ({ ...prev, [judgeId]: true }));
  };

  // Filter judges based on search term
  const filteredJudges = availableJudges.filter(judge =>
    judge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (judge.title && judge.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Competition polling API call (uses same API Gateway as other services)
  const competitionApiCall = async (endpoint, method = 'GET', body = null) => {
    const AWS_API_BASE_URL = process.env.REACT_APP_API_URL || 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev';
    const url = `${AWS_API_BASE_URL}${endpoint}`;
    const options = {
      method,
      headers: { 'Content-Type': 'application/json' },
    };
    if (body) options.body = JSON.stringify(body);
    
    try {
      const response = await fetch(url, options);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`Competition API call failed: ${method} ${endpoint}`, error);
      throw error;
    }
  };

  // Handle judge selection
  const handleJudgeSelect = (judge) => {
    // Prevent selection of already assigned judges
    if (assignedJudges.has(judge.id)) {
      return;
    }
    
    setSelectedJudgeId(judge.id);
    setSelectedJudgeProfile(judge);
    
    // Reset position selection when changing judges
    setSelectedJudgePosition('');
  };
  
  // Derived state for dropdowns
  const [availableStages, setAvailableStages] = useState([]);
  
  // Display names
  const [eventName, setEventName] = useState('');
  const [stageName, setStageName] = useState('');
  const [selectedStageData, setSelectedStageData] = useState(null);
  const [currentCompetitionCode, setCurrentCompetitionCode] = useState('');
  const [currentCompetitionJudges, setCurrentCompetitionJudges] = useState(new Set()); // Judges assigned to current competition
  const [createdCompetitionId, setCreatedCompetitionId] = useState(null); // Track created competition to prevent recreation
  
  // Judge settings from admin configuration
  const [judgeSettings, setJudgeSettings] = useState({
    numberOfJudges: 3,
    maxJudges: 5,
    loading: true
  });

  // Check global judge availability across all active competitions
  const checkGlobalJudgeAvailability = useCallback(async (sectionData = null) => {
    setPositionCheckLoading(true);
    try {
      const data = await competitionApiCall('/competitions/judge-availability');
      
      if (data && !data.error) {
        // Set globally assigned judges (judges assigned to ANY active competition)
        const globallyAssignedJudges = data.assignedJudges || [];
        setAssignedJudges(new Set(globallyAssignedJudges));
        
        // For current competition, find it by matching event, stage, and section
        let currentCompetitionPositions = new Set();
        if (eventName && stageName && data.activeCompetitions && data.occupiedPositions) {
          // Find the competition that matches our selected event, stage, and section
          const matchingCompetition = data.activeCompetitions.find(comp => {
            // Match by event name and stage name (case-insensitive)
            const eventMatch = comp.eventName && comp.eventName.toLowerCase() === eventName.toLowerCase();
            const stageMatch = comp.stageName && comp.stageName.toLowerCase() === stageName.toLowerCase();
            
            // If we have a selected section, also match by section/cluster in competition code
            if (sectionData && sectionData.id) {
              // Competition code should contain the cluster ID (e.g., cluster-5)
              const sectionMatch = comp.competitionCode && comp.competitionCode.includes(sectionData.id);
              return eventMatch && stageMatch && sectionMatch;
            }
            
            // If no section selected, fall back to event and stage matching
            return eventMatch && stageMatch;
          });
          
          if (matchingCompetition && data.occupiedPositions[matchingCompetition.competitionCode]) {
            setCurrentCompetitionCode(matchingCompetition.competitionCode);
            currentCompetitionPositions = new Set(data.occupiedPositions[matchingCompetition.competitionCode].map(p => parseInt(p)));
            
            // Store judges assigned to this specific competition
            const currentCompJudges = new Set();
            if (data.judgeAssignments && data.judgeAssignments[matchingCompetition.competitionCode]) {
              Object.keys(data.judgeAssignments[matchingCompetition.competitionCode]).forEach(judgeId => {
                currentCompJudges.add(judgeId);
              });
            }
            setCurrentCompetitionJudges(currentCompJudges);
          } else if (matchingCompetition) {
            // Still set the competition code and judges even if no occupied positions
            setCurrentCompetitionCode(matchingCompetition.competitionCode);
            const currentCompJudges = new Set();
            if (data.judgeAssignments && data.judgeAssignments[matchingCompetition.competitionCode]) {
              Object.keys(data.judgeAssignments[matchingCompetition.competitionCode]).forEach(judgeId => {
                currentCompJudges.add(judgeId);
              });
            }
            setCurrentCompetitionJudges(currentCompJudges);
          } else {
            // Clear competition-specific data when no matching competition is found
            setCurrentCompetitionCode('');
            setCurrentCompetitionJudges(new Set());
          }
        }
        setOccupiedPositions(currentCompetitionPositions);
      } else {
        // Clear assignments if there's an error
        setOccupiedPositions(new Set());
        setCurrentCompetitionCode('');
        setCurrentCompetitionJudges(new Set());
        setAssignedJudges(new Set());
      }
    } catch (error) {
      console.error('Failed to check global judge availability:', error);
      // Clear assignments on error
      setOccupiedPositions(new Set());
      setCurrentCompetitionCode('');
      setCurrentCompetitionJudges(new Set());
      setAssignedJudges(new Set());
    } finally {
      setPositionCheckLoading(false);
    }
  }, [eventName, stageName]);

  // Don't auto-select judge count - let user choose

  // Fetch HitchKick data when competition ID is provided
  useEffect(() => {
    // Only fetch if competition ID is provided and is numeric
    if (!hitchkickCompetitionId || !/^\d+$/.test(hitchkickCompetitionId)) {
      // Reset data if competition ID is invalid or empty
      if (!hitchkickCompetitionId) {
        setHitchkickData(null);
        setHitchkickStages([]);
        setHitchkickSections({});
        setEvents([]);
        setEventStages({});
        setIsDataLoading(false);
      }
      return;
    }
    
    const fetchHitchKickData = async () => {
      try {
        setIsDataLoading(true);
        setHitchkickError(null);
        setCompetitionIdError('');
        
        // Step 1: Registration opening
        setLoadingProgress('🎪 REGISTRATION OPENING SOON...');
        setLoadingPercentage(12);
        
        // Build Lambda proxy URL (no API key needed - stored securely on server)
        const url = `${HITCHKICK_CONFIG.baseUrl}/competition/${hitchkickCompetitionId}`;
        
        // Step 2: Calling performers
        setLoadingProgress('🎪 CALLING ALL PERFORMERS...');
        setLoadingPercentage(25);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: response.statusText }));
          throw new Error(`HitchKick API error: ${response.status} - ${errorData.message || response.statusText}`);
        }

        // Step 3: Gathering lineup
        setLoadingProgress('💫 GATHERING THE LINEUP...');
        setLoadingPercentage(38);

        const data = await response.json();
        
        if (!data.success || !data.payload) {
          throw new Error('Invalid response format from HitchKick API');
        }
        
        // Step 4: Reviewing program
        setLoadingProgress('🎭 REVIEWING THE PROGRAM...');
        setLoadingPercentage(50);
        
        // Handle both old and new API structures
        let scheduleEntries;
        let eventTimezone = null;
        
        // Log the payload structure for debugging
        console.log('API Response payload structure:', {
          hasPayload: !!data.payload,
          payloadType: typeof data.payload,
          isArray: Array.isArray(data.payload),
          hasScheduleEntries: !!(data.payload && data.payload.scheduleEntries),
          payloadKeys: data.payload && typeof data.payload === 'object' ? Object.keys(data.payload) : null,
          payloadSample: data.payload && typeof data.payload === 'object' ? JSON.stringify(data.payload).substring(0, 200) : data.payload
        });
        
        if (data.payload && data.payload.scheduleEntries && Array.isArray(data.payload.scheduleEntries)) {
          // New structure with nested scheduleEntries
          scheduleEntries = data.payload.scheduleEntries;
          eventTimezone = data.payload.eventTimezone;
        } else if (Array.isArray(data.payload)) {
          // Old structure with direct array
          scheduleEntries = data.payload;
        } else if (data.payload && typeof data.payload === 'object' && data.payload !== null) {
          // Check if payload itself might be the schedule data (try common keys)
          if (data.payload.data && Array.isArray(data.payload.data)) {
            scheduleEntries = data.payload.data;
          } else if (data.payload.routines && Array.isArray(data.payload.routines)) {
            scheduleEntries = data.payload.routines;
          } else if (data.payload.items && Array.isArray(data.payload.items)) {
            scheduleEntries = data.payload.items;
          } else {
            // Last resort: log the actual structure and throw
            console.error('Unrecognized API response structure. Payload:', JSON.stringify(data.payload, null, 2).substring(0, 500));
            throw new Error(`Unrecognized API response structure. Payload type: ${typeof data.payload}, isArray: ${Array.isArray(data.payload)}, keys: ${data.payload && typeof data.payload === 'object' ? Object.keys(data.payload).join(', ') : 'N/A'}`);
          }
        } else {
          throw new Error(`Unrecognized API response structure. Payload is: ${typeof data.payload} - ${data.payload === null ? 'null' : String(data.payload).substring(0, 100)}`);
        }
        
        // Process the data to extract stages and sections
        const stagesMap = new Map();
        const sectionsMap = new Map(); // Map to track sections by stage
        
        // First pass: collect all routines and awards by stage
        scheduleEntries.forEach(routine => {
          if (routine.stage) {
            const stageId = routine.stage.id;
            if (!stagesMap.has(stageId)) {
              stagesMap.set(stageId, {
                id: stageId,
                name: routine.stage.name,
                stageNum: routine.stage.stageNum,
                routines: [],
                routineCount: 0,
                sections: []
              });
              sectionsMap.set(stageId, []);
            }
            stagesMap.get(stageId).routines.push(routine);
          }
        });

        // Second pass: identify sections within each stage
        stagesMap.forEach((stage, stageId) => {
          const routines = stage.routines.sort((a, b) => {
            // Sort by start time to ensure proper order
            return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
          });

          // Helper function to format time consistently using eventTimezone
          const formatTime = (date) => {
            const options = { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            };
            if (eventTimezone) {
              options.timeZone = eventTimezone;
            }
            return date.toLocaleTimeString('en-US', options).replace(' ', '');
          };

          // Helper function to format day and date consistently using eventTimezone
          const formatDayDate = (date) => {
            const options = { weekday: 'long' };
            if (eventTimezone) {
              options.timeZone = eventTimezone;
            }
            const dayName = date.toLocaleDateString('en-US', options);
            // For date parts, we need to use a Date object in the event timezone
            const dateInTimezone = eventTimezone ? new Date(date.toLocaleString('en-US', { timeZone: eventTimezone })) : date;
            const month = dateInTimezone.getMonth() + 1;
            const day = dateInTimezone.getDate();
            return `${dayName} ${month}/${day}`;
          };

          const sections = [];

          // Generate cluster-based sections from routine data
          const routineEntries = routines.filter(routine => 
            routine.type === 'routine' && 
            routine.clusterId && 
            routine.cluster &&
            routine.parentRoutine
          );
          
          console.log(`🔄 Frontend: Found ${routineEntries.length} routine entries with cluster data for ${stage.name}`);
          
          // 🔗 CLUSTER MERGE CONFIGURATION
          // ⚠️ TEMPORARY - See CLUSTER_MERGE_IMPLEMENTATION.md for reversion instructions
          const CLUSTER_MERGE_RULES = {
            // HitchKick cluster IDs are numeric strings (e.g., "13", "14", not "cluster_13")
            '6': ['8'],
            // Merge cluster 236 into cluster 235 (for competition 2)
            '235': ['236'],
            // Merge cluster 241 into cluster 239 (for competition 5)
            '239': ['241'],
              // Merge cluster 252 into cluster 257 (for competition 17)
  '252': ['257'],
  // Merge cluster 255 into cluster 256 (for competition 17)
  '255': ['256'],
            // Add more as needed
          };

          const applyClusterMerges = (clusterGroups) => {
            const mergedGroups = { ...clusterGroups };
            
            Object.entries(CLUSTER_MERGE_RULES).forEach(([targetId, mergeIds]) => {
              if (!mergedGroups[targetId]) return;
              
              mergeIds.forEach(mergeId => {
                if (mergedGroups[mergeId]) {
                  console.log(`🔗 Frontend: Merging cluster ${mergeId} into ${targetId}`);
                  
                  mergedGroups[targetId].routines.push(...mergedGroups[mergeId].routines);
                  
                  const targetStart = new Date(mergedGroups[targetId].startTime);
                  const mergeStart = new Date(mergedGroups[mergeId].startTime);
                  const targetEnd = new Date(mergedGroups[targetId].endTime);
                  const mergeEnd = new Date(mergedGroups[mergeId].endTime);
                  
                  mergedGroups[targetId].startTime = targetStart < mergeStart ? 
                    mergedGroups[targetId].startTime : mergedGroups[mergeId].startTime;
                  mergedGroups[targetId].endTime = targetEnd > mergeEnd ? 
                    mergedGroups[targetId].endTime : mergedGroups[mergeId].endTime;
                  
                  mergedGroups[targetId].clusterIndex = Math.min(
                    mergedGroups[targetId].clusterIndex,
                    mergedGroups[mergeId].clusterIndex
                  );
                  
                  if (!mergedGroups[targetId].mergedClusters) {
                    mergedGroups[targetId].mergedClusters = [targetId];
                  }
                  mergedGroups[targetId].mergedClusters.push(mergeId);
                  
                  delete mergedGroups[mergeId];
                }
              });
            });
            
            return mergedGroups;
          };
          
          const clusterGroups = {};
          
          routineEntries.forEach(routine => {
            const clusterId = routine.clusterId;
            const cluster = routine.cluster;
            
            if (!clusterGroups[clusterId]) {
              clusterGroups[clusterId] = {
                clusterId: clusterId,
                clusterIndex: cluster.clusterIndex || 0,
                stageId: routine.stageId,
                stageName: stage.name,
                startTime: cluster.startTime,
                endTime: cluster.endTime,
                routines: []
              };
            }
            
            clusterGroups[clusterId].routines.push({
              id: routine.id,
              parentRoutineId: routine.parentRoutineId,
              number: routine.number,
              title: getSubmissionRoutineTitle(routine) || 'Unknown',
              hitchkickRoutineId: routine.id
            });
          });
          
          // 🔗 APPLY CLUSTER MERGES
          const mergedClusterGroups = applyClusterMerges(clusterGroups);
          
          // Convert cluster groups to sections and sort them
          Object.values(mergedClusterGroups)
            .filter(cluster => cluster.routines.length > 0)
            .sort((a, b) => a.clusterIndex - b.clusterIndex)
            .forEach((cluster, index) => {
              const sectionNumber = index + 1;
              
              // Sort routines by number
              const sortedRoutines = cluster.routines.sort((a, b) => 
                parseInt(a.number) - parseInt(b.number)
              );
              
              const routineNumbers = sortedRoutines.map(r => r.number);
              const routineRange = routineNumbers.length > 1 
                ? `#${routineNumbers[0]}-${routineNumbers[routineNumbers.length - 1]}`
                : `#${routineNumbers[0]}`;
              
              // Format time range
              const startTime = new Date(cluster.startTime);
              const endTime = new Date(cluster.endTime);
              const timeRange = `${formatTime(startTime)}-${formatTime(endTime)}`;
              const dayDate = formatDayDate(startTime);
              
              // Create section name (merged clusters will have extended time range)
              const sectionName = cluster.mergedClusters ? 
                `${dayDate} ${timeRange} (${routineRange}) [Merged]` :
                `${dayDate} ${timeRange} (${routineRange})`;
              
              // Get all routines from merged clusters
              const clusterIds = cluster.mergedClusters || [cluster.clusterId];
              const sectionRoutines = routineEntries.filter(r => 
                clusterIds.includes(r.clusterId)
              );
              
              sections.push({
                id: `cluster-${cluster.clusterId}`,
                name: sectionName,
                dayDate: dayDate,
                timeRange: timeRange,
                routineRange: routineRange,
                stageId: cluster.stageId,
                routines: sectionRoutines,
                routineIds: sectionRoutines.map(r => r.id), // Backend expects this for filtering
                routineCount: sectionRoutines.length,
                clusterId: cluster.clusterId,
                mergedClusters: cluster.mergedClusters,  // Track merged clusters
                clusterIndex: cluster.clusterIndex
              });
            });
          
          console.log(`✅ Frontend: Generated ${sections.length} cluster-based sections for ${stage.name}:`, 
            sections.map(s => `${s.name} (${s.routineCount} routines)`));
          
          // Fallback: if no cluster data, create one section with all routines
          if (sections.length === 0) {
            const allRoutines = routines.filter(routine => routine.type === 'routine' && routine.parentRoutine);
            if (allRoutines.length > 0) {
              const startTime = new Date(allRoutines[0].startTime);
              const endTime = new Date(allRoutines[allRoutines.length - 1].endTime);
              const routineNumbers = allRoutines.map(r => r.number || r.routineIndex).sort((a, b) => a - b);
              
              const dayDate = formatDayDate(startTime);
              const timeRange = `${formatTime(startTime)}-${formatTime(endTime)}`;
              const routineRange = routineNumbers.length > 1 
                ? `#${routineNumbers[0]}-${routineNumbers[routineNumbers.length - 1]}`
                : `#${routineNumbers[0]}`;
              
              const sectionName = `${dayDate} ${timeRange} (${routineRange})`;
              
              sections.push({
                id: `fallback-section-1`,
                name: sectionName,
                dayDate: dayDate,
                timeRange: timeRange,
                routineRange: routineRange,
                stageId: stage.id,
                routines: allRoutines,
                routineIds: allRoutines.map(r => r.id), // Backend expects this for filtering
                routineCount: allRoutines.length
              });
              
              console.log(`⚠️ Frontend: No cluster data found, created fallback section with ${allRoutines.length} routines`);
            }
          }

          // Section routine counts are already set above

          stage.sections = sections;
          sectionsMap.set(stageId, sections);
        });

        // Step 5: Organizing schedule
        setLoadingProgress('✨ ORGANIZING THE SCHEDULE...');
        setLoadingPercentage(62);

        // Calculate routine counts and sort stages
        const stages = Array.from(stagesMap.values()).map(stage => ({
          ...stage,
          routineCount: stage.routines.length
        })).sort((a, b) => a.stageNum - b.stageNum);
        
        // Step 6: Stage preparations
        setLoadingProgress('🌟 FINAL STAGE PREPARATIONS...');
        setLoadingPercentage(75);
        
        setHitchkickData(data);
        setHitchkickStages(stages);
        setHitchkickSections(sectionsMap);
        setEventTimezone(eventTimezone); // Store eventTimezone in state
        
        // Create HitchKick event as the primary data source
        const hitchkickEvent = {
          name: `HitchKick Competition ${hitchkickCompetitionId}`,
          city: eventTimezone ? `Timezone: ${eventTimezone}` : 'Competition',
          synthetic: true,
          source: 'hitchkick',
          stages: stages,
          sections: sectionsMap
        };
        
        // Set as the only event option
        setEvents([hitchkickEvent]);
        
        // Set as the only event stages option - use the exact event name
        setEventStages({
          [hitchkickEvent.name]: {
            stages: stages,
            enhanced: true,
            source: 'hitchkick'
          }
        });
        
        // Auto-select the event if not already selected
        if (!eventId || eventId !== hitchkickEvent.name) {
          setEventId(hitchkickEvent.name);
        }
        
        // Step 7: Sound check
        setLoadingProgress('🎵 SOUND CHECK COMPLETE...');
        setLoadingPercentage(87);
        
        // Brief pause to show completion
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Step 8: Show time!
        setLoadingProgress('🎉 SHOW TIME!');
        setLoadingPercentage(100);
        
        // Keep at 100% briefly for the fade-out animation
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setLoadingProgress('');
        setLoadingPercentage(0);
        
      } catch (error) {
        console.error('Failed to load HitchKick data:', error);
        const errorMessage = error.message || 'Failed to load HitchKick competition data';
        setHitchkickError(errorMessage);
        
        // Check if it's a 404 or invalid competition ID error
        if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
          setCompetitionIdError(`Competition ID ${hitchkickCompetitionId} not found. Please check the ID and try again.`);
        } else {
          setCompetitionIdError(errorMessage);
        }
        
        setError('Failed to load HitchKick competition data. Please check the competition ID and try again.');
        
        setLoadingProgress('');
        setLoadingPercentage(0);
      } finally {
        setIsDataLoading(false);
      }
    };
    
    fetchHitchKickData();
  }, [hitchkickCompetitionId]);

  // Update available stages when event changes
  useEffect(() => {
    console.log('🔄 Stage update effect triggered:', { eventId, eventsCount: events.length, eventStagesKeys: Object.keys(eventStages) });
    
    if (eventId && events.length > 0) {
      const event = events.find(e => e.name === eventId);
      console.log('🔍 Looking for event:', eventId, 'Found:', event);
      
      if (event) {
        setEventName(event.name);
        
        // Get stages for this event
        const stageConfig = eventStages[event.name];
        console.log('📋 Stage config for', event.name, ':', {
          hasStageConfig: !!stageConfig,
          hasStages: !!(stageConfig && stageConfig.stages),
          stagesCount: stageConfig?.stages?.length || 0,
          enhanced: stageConfig?.enhanced,
          synthetic: event.synthetic
        });
        
        if (stageConfig && stageConfig.stages) {
          // For enhanced/synthetic events, show all stages with routine counts > 0
          // For regular events, show stages that have schedules uploaded
          const availableStages = stageConfig.stages.filter(stage => {
            const hasRoutines = stage.routineCount > 0;
            console.log(`🎭 Stage ${stage.name} (ID: ${stage.id}): routineCount=${stage.routineCount}, hasRoutines=${hasRoutines}`);
            
            if (event.synthetic || stageConfig.enhanced) {
              // For synthetic/enhanced events, check if stage has routines
              return hasRoutines;
            } else {
              // For regular events, check if stage has schedule data
              return stage.schedule && stage.schedule.length > 0;
            }
          });
          
          console.log('✅ Setting available stages:', availableStages.length, availableStages.map(s => `${s.name} (${s.routineCount})`));
          setAvailableStages(availableStages);
        } else {
          console.warn('⚠️ No stage config or stages found for event:', event.name);
          setAvailableStages([]);
        }
        
        // Reset stage selection
        setStageId('');
        setStageName('');
        setSelectedStageData(null);
      }
    } else {
      setAvailableStages([]);
      setEventName('');
    }
  }, [eventId, events, eventStages]);

  // Update stage data when stage changes
  useEffect(() => {
    if (stageId && availableStages.length > 0) {
      const stage = availableStages.find(s => s.id.toString() === stageId.toString());
      if (stage) {
        setStageName(stage.name);
        setSelectedStageData(stage);
        
        // Update available sections for this stage
        const stageSections = hitchkickSections.get(stageId) || [];
        setAvailableSections(stageSections);
        
        // Reset section selection
        setSectionId('');
        setSelectedSectionData(null);
        
        // Note: Judge availability check moved to section selection
      }
    } else {
      setStageName('');
      setSelectedStageData(null);
      setAvailableSections([]);
      setSectionId('');
      setSelectedSectionData(null);
    }
  }, [stageId, availableStages, selectedRole, eventId, checkGlobalJudgeAvailability, hitchkickSections]);

  // Update section data when section changes
  useEffect(() => {
    if (sectionId && availableSections.length > 0) {
      const section = availableSections.find(s => s.id === sectionId);
      if (section) {
        setSelectedSectionData(section);
        
        // Check global judge availability for judges when section is selected
        if (selectedRole === 'judge' && eventId && stageId) {
          checkGlobalJudgeAvailability(section);
        }
      }
    } else {
      setSelectedSectionData(null);
    }
  }, [sectionId, availableSections, selectedRole, eventId, stageId, checkGlobalJudgeAvailability]);

  // Transform enhanced routine data to legacy Routine interface format
  const transformEnhancedRoutineToLegacy = (enhancedRoutine) => {
    return {
      id: enhancedRoutine.id || enhancedRoutine.routineIndex,
      routineNumber: enhancedRoutine.number || enhancedRoutine.routineIndex,
      name: getSubmissionRoutineTitle(enhancedRoutine) || 'Untitled Routine',
      performerName: getPerformerName(enhancedRoutine),
      studioCode: getStudioCode(enhancedRoutine),
      studioName: getStudioName(enhancedRoutine),
      category: enhancedRoutine.parentRoutine?.submissionRoutines?.[0]?.category?.name || 'Unknown',
      division: enhancedRoutine.parentRoutine?.submissionRoutines?.[0]?.division?.name || 'Unknown',
      level: enhancedRoutine.parentRoutine?.submissionRoutines?.[0]?.level?.name || 'Unknown',
      ageGroup: enhancedRoutine.parentRoutine?.submissionRoutines?.[0]?.level?.name || 'N/A',
      type: enhancedRoutine.type || 'routine',
      routineType: enhancedRoutine.parentRoutine?.routineType || 'normal',
      startTime: enhancedRoutine.startTime,
      endTime: enhancedRoutine.endTime,
      duration: calculateDuration(enhancedRoutine.startTime, enhancedRoutine.endTime),
      choreographer: enhancedRoutine.parentRoutine?.choreographer || '',
      'Song Length': enhancedRoutine.parentRoutine?.songLength || '',
      // FIXED: Use correct path for HitchKick data (same as transformHitchkickRoutineToLegacy)
      dancersCount: enhancedRoutine.parentRoutine?.submissionRoutines?.[0]?.routineDancers?.length || 
                   enhancedRoutine.parentRoutine?.routineDancers?.length || 1,
      ageUp: enhancedRoutine.parentRoutine?.ageUp || false,
      extraTime: enhancedRoutine.parentRoutine?.extraTime || '0',
      // Enhanced fields for backward compatibility
      enhanced: true,
      originalData: enhancedRoutine
    };
  };

  // Transform HitchKick routine data to legacy format (same structure as enhanced)
  const transformHitchkickRoutineToLegacy = (hitchkickRoutine, index) => {
    // Create a unique ID by combining multiple fields to avoid duplicates
    const uniqueId = hitchkickRoutine.id || 
                     `${hitchkickRoutine.routineIndex}_${hitchkickRoutine.stageId}_${index}` ||
                     `hitchkick_${index}`;
    
    const routineDancers = hitchkickRoutine.parentRoutine?.submissionRoutines?.[0]?.routineDancers;
    const dancersCount = routineDancers?.length || 1;
    
    const transformed = {
      id: uniqueId,
      routineNumber: hitchkickRoutine.number || hitchkickRoutine.routineIndex || uniqueId,
      name: getSubmissionRoutineTitle(hitchkickRoutine) || 'Untitled Routine',
      performerName: getPerformerName(hitchkickRoutine),
      studioCode: getStudioCode(hitchkickRoutine),
      studioName: getStudioName(hitchkickRoutine),
      category: hitchkickRoutine.parentRoutine?.submissionRoutines?.[0]?.category?.name || 'Unknown',
      division: hitchkickRoutine.parentRoutine?.submissionRoutines?.[0]?.division?.name || 'Unknown',
      level: hitchkickRoutine.parentRoutine?.submissionRoutines?.[0]?.level?.name || 'Unknown',
      ageGroup: hitchkickRoutine.parentRoutine?.submissionRoutines?.[0]?.level?.name || 'N/A',
      type: hitchkickRoutine.type || 'routine',
      routineType: hitchkickRoutine.parentRoutine?.routineType || 'normal',
      startTime: hitchkickRoutine.startTime,
      endTime: hitchkickRoutine.endTime,
      duration: calculateDuration(hitchkickRoutine.startTime, hitchkickRoutine.endTime),
      choreographer: hitchkickRoutine.parentRoutine?.choreographer || '',
      'Song Length': hitchkickRoutine.parentRoutine?.songLength || '',
      // STANDARD FIELD: Use consistent field name that matches existing schema
      dancersCount: dancersCount,
      ageUp: hitchkickRoutine.parentRoutine?.ageUp || false,
      extraTime: hitchkickRoutine.parentRoutine?.extraTime || '0',
      // Enhanced fields for backward compatibility
      enhanced: true,
      source: 'hitchkick',
      originalData: hitchkickRoutine,
      // Add unique key for React rendering
      key: uniqueId
    };
    
    return transformed;
  };

  // Helper functions for data extraction
  // Helper function to get submission routine title (prefers lastSubmitted, then first)
  const getSubmissionRoutineTitle = (routine) => {
    if (!routine?.parentRoutine?.submissionRoutines || 
        !Array.isArray(routine.parentRoutine.submissionRoutines) || 
        routine.parentRoutine.submissionRoutines.length === 0) {
      // Fallback to parentRoutine.title if no submission routines exist
      return routine?.parentRoutine?.title;
    }
    
    // Prefer the routine with lastSubmitted: true
    const lastSubmitted = routine.parentRoutine.submissionRoutines.find(sr => sr.lastSubmitted);
    const submissionRoutine = lastSubmitted || routine.parentRoutine.submissionRoutines[0];
    
    return submissionRoutine?.title || routine?.parentRoutine?.title;
  };

  const getPerformerName = (routine) => {
    // Try to get dancers from the correct path in HitchKick data
    const dancers = routine.parentRoutine?.submissionRoutines?.[0]?.routineDancers;
    
    if (dancers && dancers.length > 0) {
      if (dancers.length === 1) {
        // Solo - show the dancer's name
        const dancer = dancers[0].rosterDancers;
        if (dancer && dancer.firstName && dancer.lastName) {
          return `${dancer.firstName} ${dancer.lastName}`;
        }
      } else {
        // Group routine - show nothing (empty string)
        return '';
      }
    }
    
    // Fallback - return empty string instead of "Unknown Performer"
    return '';
  };

  const getStudioCode = (routine) => {
    try {
      // Check if there's an actual studioCode field first
      const actualStudioCode = routine.parentRoutine?.submissionRoutines?.[0]?.studioCode;
      if (actualStudioCode) {
        return actualStudioCode;
      }
      // If no actual studio code, return null so "N/A" fallback works
      return null;
    } catch (error) {
      return null;
    }
  };

  const getStudioName = (routine) => {
    try {
      return routine.parentRoutine?.submissionRoutines?.[0]?.registrations?.studios?.businessName || 'Unknown Studio';
    } catch (error) {
      return 'Unknown Studio';
    }
  };

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      return Math.round((end - start) / 1000); // Duration in seconds
    } catch (error) {
      return 0;
    }
  };

  const handleCreateCompetition = async () => {
    if (!eventId || !stageId || !selectedStageData) {
      setError('Please complete all selections');
      return;
    }

    // Validate section selection is required when sections are available
    if (availableSections.length > 0 && !sectionId) {
      setError('Please select a section');
      return;
    }

    // Validate judge selection if user is a judge
    if (selectedRole === 'judge') {
      if (!selectedJudgeId) {
        setError('Please select a judge profile');
        return;
      }
      if (!selectedJudgePosition) {
        setError('Please select a judge position');
        return;
      }
      
      // Note: Judge assignment conflicts are now prevented at the UI level
      // by disabling already-assigned judges in the selection grid
      
      if (occupiedPositions.has(parseInt(selectedJudgePosition))) {
        setError(`Position ${selectedJudgePosition} is already occupied by another judge`);
        return;
      }
    }

    // Role is already selected from previous page

    setIsLoading(true);
    setError('');
    
    try {
      // Step 1: Warming up
      setLoadingProgress('🎭 WARMING UP BACKSTAGE...');
      setLoadingPercentage(15);
      
      // Create display name
      const displayName = selectedSectionData 
        ? `${eventName} - ${stageName} - ${selectedSectionData.name}`
        : `${eventName} - ${stageName}`;
      
      // Step 2: Stretching and prepping
      setLoadingProgress('💃 STRETCHING AND PREPPING...');
      setLoadingPercentage(30);
      
      // 🔧 FIX: Check if competition already exists before creating
      let secureCompetitionId = createdCompetitionId;

      // If competition doesn't exist in local state, check if it exists in the database
      if (!secureCompetitionId) {
        // Try to find existing competition for this section
        if (sectionId && currentCompetitionCode) {
          console.log(`🔍 Checking if competition already exists for section: ${sectionId}`);
          console.log(`   Using competition code from state: ${currentCompetitionCode}`);
          
          try {
            // Query the competition details endpoint to see if this competition exists
            const checkResponse = await fetchWithAuth(`${API_BASE}/competition/${currentCompetitionCode}/details`, {
              method: 'GET'
            });
            
            if (checkResponse && checkResponse.competitionId) {
              console.log(`✅ Found existing competition: ${checkResponse.competitionId}`);
              secureCompetitionId = checkResponse.competitionId;
              setCreatedCompetitionId(secureCompetitionId);
              // Don't create a new competition - use the existing one
            }
          } catch (checkError) {
            console.log(`ℹ️ Competition doesn't exist yet, will create new one`);
            // Competition doesn't exist, continue to create it below
          }
        }
      } else {
        console.log(`✅ Using existing competition from state: ${secureCompetitionId}`);
      }
      
      // Prepare HitchKick schedule data for cluster-based sectioning
      let scheduleData = [];
      try {
          // Find the selected stage in HitchKick data
          const selectedHitchkickStage = hitchkickStages.find(stage => stage.id.toString() === stageId.toString());
          if (selectedHitchkickStage && selectedHitchkickStage.routines) {
            console.log(`📊 Processing HitchKick data for stage ${stageId}:`, {
              totalEntries: selectedHitchkickStage.routines.length,
              stageName: selectedHitchkickStage.name,
              selectedSection: selectedSectionData?.name || null
            });
            
            // Get summary of routine types for debugging
            getRoutineTypeSummary(selectedHitchkickStage.routines);
            
            // Prepare schedule data with cluster information
            let allRoutinesWithClusters = selectedHitchkickStage.routines.map((routine, index) => ({
              ...routine,
              // Ensure we have the cluster information for backend processing
              clusterId: routine.clusterId,
              cluster: routine.cluster,
              stageId: routine.stageId,
              // Keep original HitchKick structure for backend transformation
              originalIndex: index
            }));
            
            // Apply section filtering if a specific section is selected
            if (selectedSectionData) {
              // Get all cluster IDs (including merged ones)
              const clusterIds = selectedSectionData.mergedClusters || [selectedSectionData.clusterId];
              console.log(`🔍 Filtering for cluster IDs:`, clusterIds);
              
              // Filter to only routines in the selected cluster section(s)
              scheduleData = allRoutinesWithClusters.filter(routine => 
                clusterIds.includes(routine.clusterId)
              );
            } else {
              // No specific section selected, send all routines for full competition
              scheduleData = allRoutinesWithClusters;
              console.log(`📊 Full stage selected: ${scheduleData.length} total routines for cluster-based processing`);
            }
            
            console.log(`✅ Prepared ${scheduleData.length} HitchKick entries for backend processing`);
            console.log(`🔄 Cluster data sample:`, scheduleData.filter(r => r.clusterId).slice(0, 3).map(r => ({
              id: r.id,
              type: r.type,
              clusterId: r.clusterId,
              clusterIndex: r.cluster?.clusterIndex
            })));
          }
      } catch (scheduleError) {
        console.error('Error processing HitchKick schedule data:', scheduleError);
        scheduleData = [];
      }
      
      // Step 3: Cueing the music
      setLoadingProgress('🎵 CUEING THE MUSIC...');
      setLoadingPercentage(45);
      
      // Debug logging for sectionData
      console.log('🔍 DEBUG - selectedSectionData before API call:', selectedSectionData);
      console.log('🔍 DEBUG - sectionId:', sectionId);
      console.log('🔍 DEBUG - selectedSectionData?.routines?.length:', selectedSectionData?.routines?.length);
      console.log('🔍 DEBUG - selectedSectionData?.routineIds?.length:', selectedSectionData?.routineIds?.length);
      console.log('🔍 DEBUG - scheduleData.length being sent:', scheduleData.length);
      
      // If competition still doesn't exist, create it first
      if (!secureCompetitionId) {
        console.log('🆕 Creating new competition...');
        
        // Call V2 Competition Creation API with HitchKick integration
        const competitionResponse =
        await fetchWithAuth(`${API_BASE}/competition/create-v2`, {
          method: 'POST',
          body: {
            // HitchKick integration - reference-based approach
            eventName: eventName,
            stageName: stageName,
            stageId: stageId,
            displayName: displayName,
            judgeCount: selectedRole === 'judge' ? 1 : 5,
            
            // HitchKick API references (single source of truth)
            hitchkickEventId: hitchkickCompetitionId,
            hitchkickStageId: stageId,
            hitchkickApiEndpoint: `https://hitchkick.com/api/external/event/schedule/competition/${hitchkickCompetitionId}/table?danceDigital=true&key=lIb9l3PUHT6DeTQxnLjyRzhbr5BZbeabQKAyBjXN7c1augglgUlr189pKxWQeQ8gEP4uR6hO9wPlEoIWp5sJmwQQznUCQZ7Y8DXB64uEcfO9D9CBvI2pzKTX`,
            eventTimezone: eventTimezone, // Include eventTimezone from HitchKick data
            
            // Section filtering (if a section was selected)
            sectionId: sectionId,
            sectionName: selectedSectionData?.name || null,
            sectionData: selectedSectionData, // Include full section data with routines
            
            // Summary data only (no full schedule)
            routineCount: scheduleData?.length || 0,
            estimatedDuration: scheduleData?.length ? scheduleData.length * 3 : 0, // Estimate 3 minutes per routine
            
            accessLevel: 'private'
          }
        });
        
        if (!competitionResponse.success || !competitionResponse.competition) {
          throw new Error('Failed to create secure competition');
        }
        
        const secureCompetition = competitionResponse.competition;
        secureCompetitionId = secureCompetition.competitionId;
        setCreatedCompetitionId(secureCompetitionId);
        console.log(`✅ Created new competition: ${secureCompetitionId}`);
      } else {
        console.log(`✅ Using existing competition: ${secureCompetitionId}`);
      }
      
      // Step 4: Setting the spotlight
      setLoadingProgress('✨ SETTING THE SPOTLIGHT...');
      setLoadingPercentage(60);
      
      // If this is a judge, assign them to the competition with their selected position
      if (selectedRole === 'judge') {
        
        try {
          // Call judge assignment API
          const assignmentResponse = await fetchWithAuth(`${API_BASE}/competition/${secureCompetitionId}/assign-judge`, {
            method: 'POST',
            body: {
              judgeId: selectedJudgeId,
              judgeProfile: selectedJudgeProfile,
              position: parseInt(selectedJudgePosition),
              accessLevel: 'judge' // Judge-level access
            }
          });
          
          if (assignmentResponse.judgeId && assignmentResponse.assignedPosition) {
            // Note: Unified system doesn't use access tokens - uses standard access control
          } else {
            console.warn('Judge assignment response format unexpected:', assignmentResponse);
          }
        } catch (assignError) {
          console.warn('Judge assignment API not ready, will assign during session start');
          // Continue - the session start will handle assignment
        }
      }
      
      // Step 5: Final touches on costumes
      setLoadingProgress('🌟 FINAL TOUCHES ON COSTUMES...');
      setLoadingPercentage(75);
      
      // Start the session using the secure competition ID
      const judgeId = selectedRole === 'judge' ? selectedJudgeId : null;
      const judgeProfileData = selectedRole === 'judge' ? selectedJudgeProfile : null;
      const judgePosition = selectedRole === 'judge' ? selectedJudgePosition : null;
      await startSession(secureCompetitionId, selectedRole, scheduleData, judgeId, judgeProfileData, judgePosition);
      
      // Step 6: Opening the curtains
      setLoadingProgress('🎪 OPENING THE CURTAINS...');
      setLoadingPercentage(90);
      
      // Save session data to context/localStorage with secure ID
      saveCompetitionData({
        competitionId: secureCompetitionId,
        competitionCode: secureCompetitionId,
        role: selectedRole,
        judgeId: judgeId,
        judgeProfile: judgeProfileData,
        judgePosition: judgePosition,
        displayName,
        eventName: eventName,
        stageName: stageName,
        stageId: stageId,
        sectionId: sectionId,
        sectionData: selectedSectionData,
        scheduleData: scheduleData,
        securityLevel: 'v2-secure' // Mark as secure
        // organizerId not needed here - it's stored in competition metadata
      });
      
      // Step 7: Ready to perform!
      setLoadingProgress('🎉 READY TO PERFORM!');
      setLoadingPercentage(100);
      
      // Brief pause for celebration
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Navigate using the secure competition ID
      if (selectedRole === 'tabulator') {
        // Pass schedule data to tabulator page so routines are loaded
        navigate(`/competition/${secureCompetitionId}/tabulator`, {
          state: {
            competitionName: displayName,
            eventName: eventName,
            stageName: stageName,
            scheduleData: scheduleData || []
          }
        });
      } else {
        // For judges, navigate to judge station (uses standard access control)
        navigate(`/competition/${secureCompetitionId}/judge/${judgeId}/${judgePosition}`);
      }
      
    } catch (err) {
      console.error('Error creating secure competition:', err);
      // Provide more specific error messages
      if (err.message.includes('organization')) {
        setError('You must be part of an organization to create competitions. Please contact support.');
      } else if (err.message.includes('assign-judge')) {
        setError('Failed to assign judge to competition. Please try again.');
      } else if (err.message.includes('create-v2')) {
        setError('Competition creation service is temporarily unavailable. Please try again.');
      } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        setError('You are not authorized to create competitions. Please check your permissions.');
      } else {
        setError('Failed to create secure competition. Please try again.');
      }
      
      // Clear loading messages on error
      setLoadingProgress('');
      setLoadingPercentage(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle unassigning a judge from a competition
  const handleUnassignJudge = (judgeId) => {
    if (!judgeId) {
      setError('Judge ID is required for unassignment');
      return;
    }

    // Find the judge to get their name for the modal
    const judge = availableJudges.find(j => j.id === judgeId);
    setJudgeToUnassign(judge);
    setShowUnassignModal(true);
  };

  // Handle navigating to judge station for already assigned judge - simplified approach
  const handleAccessJudgeStation = (judge) => {
    if (!judge) {
      setError('Judge information is required');
      return;
    }

    if (!currentCompetitionCode) {
      setError('Competition not found. Please refresh the page and try again.');
      return;
    }

    try {
      // Use position "1" as default - the system will handle the correct assignment
      const judgePosition = "1";
      
      // Construct the URL directly using the stored competition code
      const judgeStationUrl = `/competition/${currentCompetitionCode}/judge/${judge.id}/${judgePosition}`;
      
      // Open in new tab with state information
      const newTab = window.open(judgeStationUrl, '_blank');
      
      // Store judge profile data in sessionStorage for the new tab to access
      const judgeStationData = {
        judgeProfile: judge,
        competitionName: `${eventName} - ${stageName}`,
        eventName: eventName,
        stageName: stageName,
        timestamp: Date.now()
      };
      
      sessionStorage.setItem(`judgeStation_${judge.id}`, JSON.stringify(judgeStationData));
      
      if (!newTab) {
        setError('Pop-up blocked. Please allow pop-ups and try again.');
      }
      
    } catch (error) {
      console.error('Error accessing judge station:', error);
      setError('Failed to access judge station. Please try again.');
    }
  };

  // Confirm unassignment (called from modal)
  const confirmUnassignJudge = async () => {
    if (!judgeToUnassign) return;

    const judgeId = judgeToUnassign.id;
    const judgeName = judgeToUnassign.name || `Judge ${judgeId}`;

    try {
      setIsUnassigning(true);
      setError('');
      
      // Use the currentCompetitionCode that was already determined by checkGlobalJudgeAvailability
      // This is more reliable than trying to reconstruct the competition code
      let competitionCode = currentCompetitionCode;
      
      // If we don't have a current competition code, try to find it
      if (!competitionCode) {
        const activeCompetitionsData = await competitionApiCall('/competitions/judge-availability');
        
        if (activeCompetitionsData?.activeCompetitions && eventName && stageName) {
          // Find the competition that matches our selected event and stage
          const matchingCompetition = activeCompetitionsData.activeCompetitions.find(comp => {
            const eventMatch = comp.eventName && comp.eventName.toLowerCase() === eventName.toLowerCase();
            const stageMatch = comp.stageName && comp.stageName.toLowerCase() === stageName.toLowerCase();
            return eventMatch && stageMatch;
          });
          
          if (matchingCompetition) {
            competitionCode = matchingCompetition.competitionCode;
          }
        }
      }
      
      // If still no competition code, try to find any competition where this judge is assigned
      if (!competitionCode) {
        const activeCompetitionsData = await competitionApiCall('/competitions/judge-availability');
        
        if (activeCompetitionsData?.judgeAssignments) {
          // Look through all competitions to find where this judge is assigned
          for (const [compCode, judges] of Object.entries(activeCompetitionsData.judgeAssignments)) {
            if (judges[judgeId]) {
              competitionCode = compCode;
              break;
            }
          }
        }
      }
      
      // If still no competition code, show error
      if (!competitionCode) {
        throw new Error('Cannot find the competition to unassign judge from. The judge may not be currently assigned to any active competition.');
      }
      
      // Verify judge is actually assigned to current competition
      if (!currentCompetitionJudges.has(judgeId)) {
        // The judge is assigned globally but not to this competition
        // We need to find which competition they're actually assigned to
        const activeCompetitionsData = await competitionApiCall('/competitions/judge-availability');
        
        if (activeCompetitionsData?.judgeAssignments) {
          // Look through all competitions to find where this judge is assigned
          for (const [compCode, judges] of Object.entries(activeCompetitionsData.judgeAssignments)) {
            if (judges[judgeId]) {
              competitionCode = compCode;
              break;
            }
          }
        }
        
        // If we still haven't found the judge, they might not be assigned anywhere
        if (!activeCompetitionsData?.judgeAssignments || !Object.values(activeCompetitionsData.judgeAssignments).some(judges => judges[judgeId])) {
          throw new Error(`Judge ${judgeName} does not appear to be assigned to any active competition. They may have already been unassigned.`);
        }
      }
      
      const response = await fetchWithAuth(`${API_BASE}/competition/${encodeURIComponent(competitionCode)}/unassign-judge`, {
        method: 'POST',
        body: { judgeId }
      });
      
      if (response && !response.error) {
        // Close modal
        setShowUnassignModal(false);
        setJudgeToUnassign(null);
        
        // Refresh global judge availability to update UI
        await checkGlobalJudgeAvailability(selectedSectionData);
        
        // Show success toast
        success(
          'Judge Unassigned Successfully',
          `${judgeName} has been unassigned and is now available for reassignment.`
        );
      } else {
        throw new Error(response?.error || 'Failed to unassign judge');
      }
      
    } catch (err) {
      console.error('Error unassigning judge:', err);
      
      // Handle specific error cases
      let errorTitle = 'Unassignment Failed';
      let errorMessage = `Failed to unassign ${judgeName}. Please try again.`;
      
      // Try to extract more specific error information from the response
      if (err.responseData && err.responseData.error) {
        errorMessage = `Failed to unassign ${judgeName}. Backend error: ${err.responseData.error}`;
      }
      
      if (err.message && err.message.includes('404')) {
        errorTitle = 'Competition Not Found';
        errorMessage = `Cannot unassign ${judgeName} because the competition was not found. This may happen if the competition hasn't been started yet or has been deleted.`;
      } else if (err.message && err.message.includes('400')) {
        // Parse the 400 error for more specific information
        if (err.responseData && err.responseData.error) {
          if (err.responseData.error.includes('not currently assigned')) {
            errorTitle = 'Judge Not Assigned';
            errorMessage = `${judgeName} is not currently assigned to this competition, so no unassignment is needed.`;
          } else if (err.responseData.error.includes('Competition code and judge ID required')) {
            errorTitle = 'Invalid Request';
            errorMessage = `Invalid request format. Please refresh the page and try again.`;
          } else {
            errorTitle = 'Invalid Request';
            errorMessage = `Cannot unassign ${judgeName}. ${err.responseData.error}`;
          }
        } else if (err.message.includes('not currently assigned')) {
          errorTitle = 'Judge Not Assigned';
          errorMessage = `${judgeName} is not currently assigned to this competition, so no unassignment is needed.`;
        } else {
          errorTitle = 'Invalid Request';
          errorMessage = `Cannot unassign ${judgeName}. ${err.message}`;
        }
      } else if (err.message && err.message.includes('Cannot find the competition')) {
        errorTitle = 'Competition Not Found';
        errorMessage = err.message;
      } else if (err.message) {
        errorMessage = `Failed to unassign ${judgeName}. ${err.message}`;
      }
      
      // Show error toast
      showError(errorTitle, errorMessage);
    } finally {
      setIsUnassigning(false);
    }
  };

  // Cancel unassignment (called from modal)
  const cancelUnassignJudge = () => {
    setShowUnassignModal(false);
    setJudgeToUnassign(null);
    setIsUnassigning(false);
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Stage Background - matching homepage */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-blue-900/20 to-indigo-900/20"></div>
      
      {/* Animated background particles */}
      <div className="absolute inset-0">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-500/30 rounded-full"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>
      
      {/* Main content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="max-w-2xl w-full"
        >
          {/* Header with logo */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center mb-6"
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="relative"
              >
                <img 
                  src={ddLogo}
                  alt="Dance Digital"
                  className="w-16 h-16 object-contain relative z-10"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl blur-xl opacity-60"></div>
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="inline-flex items-center px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-400/40 text-base text-blue-200 mb-6 backdrop-blur-xl"
            >
              <Activity className="w-4 h-4 mr-2" />
              <span className="font-semibold">Tabulator Setup</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 whitespace-nowrap"
            >
              <span className="text-white drop-shadow-2xl">SELECT </span>
              <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-cyan-400 bg-clip-text text-transparent drop-shadow-2xl">EVENT & STAGE</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-lg text-white/80 font-medium leading-relaxed mb-8 max-w-xl mx-auto"
            >
              Choose your event and stage to start your 
              <span className="text-blue-300 font-bold"> {selectedRole}</span> session with 
              <span className="text-indigo-300 font-bold"> seamless scoring</span>.
            </motion.p>
          </div>
          
          {/* Main form card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="relative"
          >
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-2xl blur-xl"></div>
            
            {/* Card content */}
            <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl p-6">
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-500/20 border border-red-500/40 rounded-xl text-white flex items-center backdrop-blur-sm"
                >
                  <AlertCircle size={18} className="text-red-400 mr-3 flex-shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}
              
              
                {/* Form fields */}
                <div className="space-y-4 mb-6">
                {/* Competition ID Input */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-white/90 mb-2">
                    <Activity className="w-4 h-4 inline mr-2 text-blue-400" />
                    HitchKick Competition ID *
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={hitchkickCompetitionId}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow numeric input
                      if (value === '' || /^\d+$/.test(value)) {
                        setHitchkickCompetitionId(value);
                        setCompetitionIdError('');
                        setError('');
                        // Reset selections when competition ID changes
                        setEventId('');
                        setStageId('');
                        setSectionId('');
                      }
                    }}
                    placeholder="Enter competition ID (e.g., 1, 4)"
                    className={`w-full py-3 px-4 border rounded-xl focus:outline-none focus:ring-2 transition-colors backdrop-blur-sm ${
                      competitionIdError
                        ? 'bg-red-500/10 border-red-400/50 text-white focus:border-red-400 focus:ring-red-400/50'
                        : 'bg-white/5 border-white/10 text-white focus:border-blue-400/50 focus:ring-blue-400/50'
                    }`}
                  />
                  {competitionIdError && (
                    <p className="text-xs text-red-400 mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {competitionIdError}
                    </p>
                  )}
                  {!competitionIdError && hitchkickCompetitionId && (
                    <p className="text-xs text-white/60 mt-1">
                      Fetching schedule for Competition {hitchkickCompetitionId}...
                    </p>
                  )}
                  {!hitchkickCompetitionId && (
                    <p className="text-xs text-white/60 mt-1">
                      Enter the HitchKick competition ID to load the schedule (e.g., 1, 4, etc.)
                    </p>
                  )}
                </div>
                
                {/* Event Selection */}
                <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white/90 mb-2">
                      <Calendar className="w-4 h-4 inline mr-2 text-blue-400" />
                      HitchKick Event
                    </label>
                    
                  {/* HitchKick Status - Show error if loading failed */}
                  {hitchkickError && (
                    <div className="flex items-center space-x-2 p-2 bg-red-500/10 border border-red-400/20 rounded-lg text-red-400 text-xs mb-2">
                      <AlertCircle className="w-3 h-3" />
                      <span>Failed to load HitchKick data: {hitchkickError}</span>
                    </div>
                  )}
                  <div className="relative">
                    <select
                      value={eventId}
                      onChange={(e) => setEventId(e.target.value)}
                      disabled={isDataLoading || !hitchkickCompetitionId || !/^\d+$/.test(hitchkickCompetitionId)}
                      className={`w-full py-3 px-4 border rounded-xl focus:outline-none focus:ring-0 transition-colors backdrop-blur-sm appearance-none ${
                        !isDataLoading && hitchkickCompetitionId && /^\d+$/.test(hitchkickCompetitionId)
                          ? 'bg-white/5 border-white/10 text-white focus:border-blue-400/50' 
                          : 'bg-white/5 border-white/5 text-white/30 cursor-not-allowed'
                      }`}
                    >
                      <option value="" disabled>
                        {!hitchkickCompetitionId || !/^\d+$/.test(hitchkickCompetitionId)
                          ? 'Enter competition ID first...'
                          : isDataLoading 
                          ? 'Loading HitchKick events...' 
                          : 'Select event...'}
                      </option>
                      {events.map(event => (
                        <option key={event.name} value={event.name} className="bg-slate-800">
                          {event.name} - {event.city}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 w-5 h-5 pointer-events-none" />
                  </div>
                  {events.length === 0 && !isDataLoading && (
                    <p className="text-sm text-yellow-400 mt-2">
                      No HitchKick events loaded. Please refresh the page to try again.
                    </p>
                  )}
                </div>
                
                {/* Stage Selection */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-white/90 mb-2">
                    <Monitor className="w-4 h-4 inline mr-2 text-blue-400" />
                    Stage
                  </label>
                  <div className="relative">
                    <select
                      value={stageId}
                      onChange={(e) => setStageId(e.target.value)}
                      disabled={!eventId || availableStages.length === 0}
                      className={`w-full py-3 px-4 border rounded-xl focus:outline-none focus:ring-0 transition-colors backdrop-blur-sm appearance-none ${
                        eventId && availableStages.length > 0
                          ? 'bg-white/5 border-white/10 text-white focus:border-blue-400/50' 
                          : 'bg-white/5 border-white/5 text-white/30 cursor-not-allowed'
                      }`}
                    >
                      <option value="" disabled>
                        {!eventId 
                          ? 'Select event first...' 
                          : availableStages.length === 0 
                          ? 'No stages with schedules found...' 
                          : 'Select stage...'}
                      </option>
                      {availableStages.map(stage => (
                        <option key={stage.id} value={stage.id} className="bg-slate-800">
                          {stage.name} ({stage.routineCount} routines)
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 w-5 h-5 pointer-events-none" />
                  </div>
                  {eventId && availableStages.length === 0 && (
                    <p className="text-sm text-yellow-400 mt-2">
                      No stages with routines found for this event. Please check HitchKick data.
                    </p>
                  )}
                </div>

                {/* Section Selection */}
                {stageId && availableSections.length > 0 && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white/90 mb-2">
                      <Award className="w-4 h-4 inline mr-2 text-purple-400" />
                      Section
                    </label>
                    <div className="relative">
                      <select
                        value={sectionId}
                        onChange={(e) => setSectionId(e.target.value)}
                        className="w-full py-3 px-4 border rounded-xl focus:outline-none focus:ring-0 transition-colors backdrop-blur-sm appearance-none bg-white/5 border-white/10 text-white focus:border-purple-400/50"
                      >
                        <option value="" className="bg-slate-800">
                          Select a section
                        </option>
                        {availableSections.map(section => (
                          <option key={section.id} value={section.id} className="bg-slate-800">
                            {section.name} ({section.routineCount} routines)
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 w-5 h-5 pointer-events-none" />
                    </div>
                    <p className="text-xs text-white/60 mt-1">
                      Select a specific section for this competition
                    </p>
                  </div>
                )}

              </div>

              {/* Compact Selection Summary - Inline (for judges, show before judge selection) */}
              {eventId && stageId && selectedStageData && selectedRole === 'judge' && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-lg border border-blue-400/20 backdrop-blur-sm mt-2 mb-4">
                    <div className="flex items-center space-x-4 text-xs">
                      <div className="flex items-center">
                        <CheckCircle className="w-3.5 h-3.5 mr-1.5 text-green-400" />
                        <span className="text-white/70">{eventName}</span>
                      </div>
                      <div className="w-px h-4 bg-white/20"></div>
                      <div className="flex items-center">
                        <Monitor className="w-3.5 h-3.5 mr-1.5 text-blue-400" />
                        <span className="text-white/70">{stageName}</span>
                      </div>
                      <div className="w-px h-4 bg-white/20"></div>
                      <div className="flex items-center">
                        <CalendarDays className="w-3.5 h-3.5 mr-1.5 text-blue-400" />
                        <span className="text-white font-medium">
                          {selectedSectionData 
                            ? `${selectedSectionData.routineCount} routines (${selectedSectionData.name})`
                            : `${selectedStageData.routineCount} routines`
                          }
                        </span>
                      </div>
                      {selectedRole && (
                        <>
                          <div className="w-px h-4 bg-white/20"></div>
                          <div className="flex items-center">
                            <Users className="w-3.5 h-3.5 mr-1.5 text-indigo-400" />
                            <span className="text-white/70 capitalize">{selectedRole}</span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center text-xs">
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-md font-medium">Ready</span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Judge Selection - Only for Judges */}
              {eventId && stageId && sectionId && selectedRole === 'judge' && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="space-y-4"
                >
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-white/90 flex items-center">
                      <Users className="w-4 h-4 mr-2 text-green-400" />
                      Select Judge Profile *
                    </label>
                    
                    {judgesLoading ? (
                      <div className="flex items-center justify-center p-8">
                        <Loader2 className="w-6 h-6 animate-spin text-green-400 mr-3" />
                        <span className="text-white/70">Loading judge profiles...</span>
                      </div>
                    ) : availableJudges.length > 0 ? (
                      <>
                        {/* Search Bar */}
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-white/60" />
                          </div>
                          <input
                            type="text"
                            placeholder="Search judges..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800/80 border border-slate-600/50 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent backdrop-blur-sm"
                          />
                        </div>
                        
                        {/* Judge Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                          {filteredJudges.map(judge => {
                            const isAssigned = assignedJudges.has(judge.id);
                            const isAssignedToCurrentCompetition = currentCompetitionJudges.has(judge.id);
                            const isSelected = selectedJudgeProfile?.id === judge.id;
                            
                            return (
                              <div key={judge.id} className="relative">
                                <motion.div
                                  onClick={() => handleJudgeSelect(judge)}
                                  whileHover={!isAssigned ? { scale: 1.03 } : {}}
                                  whileTap={!isAssigned ? { scale: 0.98 } : {}}
                                  className={`relative p-4 rounded-lg border transition-all ${
                                    isAssigned
                                      ? 'bg-gray-800/30 border-gray-600/30 cursor-not-allowed opacity-50'
                                      : isSelected
                                        ? 'bg-green-600/30 border-green-500/50 ring-2 ring-green-400/30 cursor-pointer'
                                        : 'bg-slate-800/50 border-slate-600/30 hover:bg-slate-700/50 hover:border-green-400/30 cursor-pointer'
                                  }`}
                                >
                              <div className="flex flex-col items-center text-center space-y-2">
                                <div className="w-16 h-16 rounded-full overflow-hidden bg-green-500/20 flex items-center justify-center border-2 border-green-400/30">
                                  {judge.photoUrl && !failedImages[judge.id] ? (
                                    <img 
                                      src={judge.photoUrl} 
                                      alt={judge.name}
                                      className="w-full h-full object-cover"
                                      onError={() => handleImageError(judge.id)}
                                    />
                                  ) : (
                                    <span className="text-2xl text-green-400 font-bold">
                                      {judge.name.charAt(0)}
                                    </span>
                                  )}
                                </div>
                                <div className="min-w-0 w-full">
                                  <div className="font-medium text-sm text-white truncate">{judge.name}</div>
                                  {judge.title && (
                                    <div className="text-xs text-green-400/80 truncate">{judge.title}</div>
                                  )}
                                  {judge.organization && (
                                    <div className="text-xs text-white/60 truncate mt-1">{judge.organization}</div>
                                  )}
                                </div>
                                
                                {/* Assigned Badge */}
                                {isAssigned && (
                                  <div className="absolute top-2 left-2">
                                    <div className="bg-red-500/80 text-white text-xs px-2 py-1 rounded-full font-medium">
                                      ASSIGNED
                                    </div>
                                  </div>
                                )}
                                
                                {/* Selected Checkmark */}
                                {isSelected && !isAssigned && (
                                  <CheckCircle className="w-5 h-5 text-green-400 absolute top-2 right-2" />
                                )}
                              </div>
                            </motion.div>
                            
                            {/* Action Buttons for Assigned Judges */}
                            {isAssigned && (
                              <div className="absolute top-2 right-2 flex gap-1 z-20">
                                {/* Access Judge Station Button - Only show if assigned to current competition */}
                                {isAssignedToCurrentCompetition && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAccessJudgeStation(judge);
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-full font-medium transition-colors shadow-lg"
                                    title={`Access ${judge.name}'s Judge Station`}
                                    disabled={isLoading}
                                  >
                                    <Eye className="w-3 h-3" />
                                  </button>
                                )}
                                
                                {/* Unassign Button - Always show for assigned judges */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnassignJudge(judge.id);
                                  }}
                                  className="bg-red-600 hover:bg-red-700 text-white text-xs px-2 py-1 rounded-full font-medium transition-colors shadow-lg"
                                  title={`Unassign ${judge.name}`}
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                          </div>
                            );
                          })}
                        </div>
                        
                        {filteredJudges.length === 0 && searchTerm && (
                          <div className="text-center p-6 border border-slate-600/30 rounded-lg bg-slate-800/30">
                            <Search className="h-8 w-8 mx-auto mb-3 text-white/40" />
                            <p className="text-sm text-white/80">No judges found matching "{searchTerm}"</p>
                            <button
                              onClick={() => setSearchTerm('')}
                              className="text-xs text-green-400 hover:text-green-300 mt-2"
                            >
                              Clear search
                            </button>
                          </div>
                        )}
                        
                        {selectedJudgeProfile && (
                          <div className="space-y-4">
                            {/* Position Selection */}
                            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Select Judge Position
                {positionCheckLoading && (
                  <span className="text-xs text-yellow-400 ml-2">(Checking availability...)</span>
                )}
                {judgeSettings.loading && (
                  <span className="text-xs text-blue-400 ml-2">(Loading judge settings...)</span>
                )}
                {!judgeSettings.loading && (
                  <span className="text-xs text-white/60 ml-2">
                    ({judgeSettings.maxJudges} positions available, {judgeSettings.numberOfJudges} minimum required)
                  </span>
                )}
              </label>
                              
                              
              <select
                value={selectedJudgePosition}
                onChange={(e) => setSelectedJudgePosition(e.target.value)}
                disabled={positionCheckLoading || judgeSettings.loading}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-400/50 focus:border-green-400/50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <option value="">Choose position...</option>
                                {Array.from({ length: judgeSettings.maxJudges }, (_, i) => i + 1).map(position => {
                                  const isOccupied = occupiedPositions.has(position);
                                  return (
                                    <option 
                                      key={position} 
                                      value={position}
                                      disabled={isOccupied}
                                      className={isOccupied ? 'text-red-400' : ''}
                                    >
                                      Judge Position {position} {isOccupied ? '(Occupied)' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                              
                              {/* Position Status Messages */}
                              {selectedJudgePosition && occupiedPositions.has(parseInt(selectedJudgePosition)) && (
                                <p className="text-xs text-red-400 mt-1">
                                  ❌ Position {selectedJudgePosition} is already occupied by another judge
                                </p>
                              )}
                              
              {selectedJudgePosition && !occupiedPositions.has(parseInt(selectedJudgePosition)) && (
                <p className="text-xs text-green-400 mt-1">
                  ✅ {selectedJudgeProfile.name} will be assigned to Position {selectedJudgePosition}
                </p>
              )}
                            </div>
                            
                            {/* Teacher Critique Selection */}
                            <div>
                              <label className="flex items-center space-x-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedJudgeProfile?.isTeacherCritique || false}
                                  onChange={(e) => {
                                    if (selectedJudgeProfile) {
                                      setSelectedJudgeProfile({
                                        ...selectedJudgeProfile,
                                        isTeacherCritique: e.target.checked
                                      });
                                    }
                                  }}
                                  className="w-4 h-4 text-orange-500 bg-slate-800/50 border-slate-600/30 rounded focus:ring-orange-400/50 focus:ring-2"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-white/90">Teacher Critique Judge</span>
                                  <p className="text-xs text-white/60 mt-1">
                                    Mark this judge as a Teacher Critique judge. They will be tagged as "TC" in the live score monitor.
                                  </p>
                                </div>
                              </label>
                            </div>
                            
                            <div className="flex justify-center">
                              <motion.button 
                                onClick={() => {
                                  setSelectedJudgeId('');
                                  setSelectedJudgeProfile(null);
                                  setSelectedJudgePosition('');
                                }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="px-4 py-2 text-sm bg-slate-800/50 hover:bg-slate-700/50 rounded-lg text-white/70 border border-slate-600/30"
                              >
                                Clear Selection
                              </motion.button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center p-8 border border-yellow-400/20 rounded-lg bg-yellow-500/10">
                        <User className="h-12 w-12 mx-auto mb-4 text-yellow-400" />
                        <p className="text-lg font-medium text-yellow-400 mb-2">No Judge Profiles Found</p>
                        <p className="text-sm text-white/70 mb-4">
                          You need to create judge profiles in your Admin Settings first.
                        </p>
                        <p className="text-xs text-white/60">
                          Go to Admin → Judges to create and manage judge profiles.
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            
              {/* Compact Selection Summary - Inline (for non-judges like tabulators) */}
              {eventId && stageId && selectedStageData && selectedRole !== 'judge' && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-lg border border-blue-400/20 backdrop-blur-sm mt-2 mb-4">
                    <div className="flex items-center space-x-4 text-xs">
                      <div className="flex items-center">
                        <CheckCircle className="w-3.5 h-3.5 mr-1.5 text-green-400" />
                        <span className="text-white/70">{eventName}</span>
                      </div>
                      <div className="w-px h-4 bg-white/20"></div>
                      <div className="flex items-center">
                        <Monitor className="w-3.5 h-3.5 mr-1.5 text-blue-400" />
                        <span className="text-white/70">{stageName}</span>
                      </div>
                      <div className="w-px h-4 bg-white/20"></div>
                      <div className="flex items-center">
                        <CalendarDays className="w-3.5 h-3.5 mr-1.5 text-blue-400" />
                        <span className="text-white font-medium">
                          {selectedSectionData 
                            ? `${selectedSectionData.routineCount} routines (${selectedSectionData.name})`
                            : `${selectedStageData.routineCount} routines`
                          }
                        </span>
                      </div>
                      {selectedRole && (
                        <>
                          <div className="w-px h-4 bg-white/20"></div>
                          <div className="flex items-center">
                            <Users className="w-3.5 h-3.5 mr-1.5 text-indigo-400" />
                            <span className="text-white/70 capitalize">{selectedRole}</span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center text-xs">
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-md font-medium">Ready</span>
                    </div>
                  </div>
                </motion.div>
              )}
            
              {/* Action buttons */}
              <div className="space-y-3 pt-2">
                <motion.div
                  whileHover={!isLoading && !isDataLoading ? { scale: 1.02, y: -2 } : {}}
                  whileTap={!isLoading && !isDataLoading ? { scale: 0.98 } : {}}
                  className="group"
                >
                  <button
                    onClick={handleCreateCompetition}
                    disabled={!hitchkickCompetitionId || !/^\d+$/.test(hitchkickCompetitionId) || !eventId || !stageId || !selectedStageData || (selectedRole === 'judge' && (!selectedJudgeId || !selectedJudgePosition)) || isLoading || isDataLoading}
                    className={`w-full rounded-xl font-bold text-lg relative overflow-hidden border transition-all duration-500 ${
                      hitchkickCompetitionId && /^\d+$/.test(hitchkickCompetitionId) && eventId && stageId && selectedStageData && (selectedRole !== 'judge' || (selectedJudgeId && selectedJudgePosition)) && !isLoading && !isDataLoading
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-2xl hover:shadow-blue-500/30 border-blue-400/50'
                        : 'bg-white/5 text-white/30 cursor-not-allowed border-white/10'
                    }`}
                    style={{ minHeight: '56px' }}
                  >
                    {/* Hover effect for enabled state */}
                    {hitchkickCompetitionId && /^\d+$/.test(hitchkickCompetitionId) && eventId && stageId && selectedStageData && (selectedRole !== 'judge' || (selectedJudgeId && selectedJudgePosition)) && !isLoading && !isDataLoading && (
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-20 transition-opacity" />
                    )}
                    
                    {/* Loading State (for both initial load and session start) */}
                    {(isLoading || isDataLoading) ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-0"
                      >
                        {/* Animated gradient background fill */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-500/40 via-indigo-500/40 to-purple-500/40"
                          initial={{ width: 0 }}
                          animate={{ 
                            width: `${loadingPercentage}%`,
                            opacity: loadingPercentage === 100 ? [1, 1, 0] : 1
                          }}
                          transition={{ 
                            width: { duration: 0.5, ease: 'easeOut' },
                            opacity: loadingPercentage === 100 ? { duration: 0.6, times: [0, 0.7, 1] } : { duration: 0 }
                          }}
                        >
                          {/* Shimmer effect */}
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
                          />
                          
                          {/* Glow effect */}
                          <div 
                            className="absolute inset-0 bg-gradient-to-r from-blue-400/50 via-indigo-400/50 to-purple-400/50 blur-xl"
                          />
                        </motion.div>
                        
                        {/* Content on top - Fun comments as main focus */}
                        <motion.div 
                          className="relative z-10 flex items-center justify-center px-6 py-4"
                          animate={{ 
                            opacity: loadingPercentage === 100 ? [1, 1, 0] : 1 
                          }}
                          transition={{ 
                            opacity: loadingPercentage === 100 ? { duration: 0.6, times: [0, 0.7, 1] } : { duration: 0 }
                          }}
                        >
                          <p className="text-base font-bold text-blue-50 uppercase tracking-wide drop-shadow-lg">{loadingProgress || '🎭 LOADING...'}</p>
                        </motion.div>
                      </motion.div>
                    ) : (
                      /* Normal Button State */
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="flex items-center justify-center space-x-3 py-4 px-6"
                      >
                        <Zap size={20} />
                        <span>Start Session</span>
                      </motion.div>
                    )}
                  </button>
                </motion.div>
                
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <button
                    onClick={() => navigate('/competition/portal')}
                    className="w-full py-3 px-6 rounded-xl font-semibold text-white/80 transition-all duration-300 flex items-center justify-center space-x-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20"
                  >
                    <ArrowLeft size={18} />
                    <span>Back to Portal</span>
                  </button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Unassign Judge Modal */}
      <UnassignJudgeModal
        isOpen={showUnassignModal}
        onConfirm={confirmUnassignJudge}
        onCancel={cancelUnassignJudge}
        judgeName={judgeToUnassign?.name || 'Unknown Judge'}
        judgeId={judgeToUnassign?.id || ''}
        isLoading={isUnassigning}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};



export default CompetitionCreation;